import { byIso, byInternet } from 'country-code-lookup';
import { addDays, differenceInCalendarYears, differenceInDays } from 'date-fns';
import { get } from 'lodash';

import { AnalyticsOrderType, DATA_TYPE_GMV, DATA_TYPE_UNITS, DEFAULT_REPORT_STATE } from '../constants/analytics';
import { SortDirection, SortKey } from './tableHelper';
import { REPORT_TYPES } from '../components/tables/reports/consts';
import { jsonStringToObject } from './common';
import { formatDateGraphXAxis } from './analytics-granularity';
import { getDefaultColumns } from '../components/tables/reports/reportsColumnNames/utils';

export const toUiSalesValue = (originalValue) => originalValue / 100; // original value is in cents

const byBestSellers = (modelRecordA, modelRecordB) => {
  const valuesA = Object.values(modelRecordA).filter((value) => typeof value === 'number');
  const valuesB = Object.values(modelRecordB).filter((value) => typeof value === 'number');
  const sumValuesA = valuesA.reduce((sum, value) => (sum += value), 0);
  const sumValuesB = valuesB.reduce((sum, value) => (sum += value), 0);

  return sumValuesA - sumValuesB;
};

export function prepareDataForBestSellersGraph({ rawData, currentCurrency, retailerNamesByIds }) {
  const keys = new Set();
  const uiData = {
    gmv: [],
    units: [],
    orders: [],
  };

  rawData.gmv.forEach((modelRecord) => {
    const { model, retailers, product_image: productImage, product_name: productName } = modelRecord;
    const retailersData = retailers.reduce((retailersData, retailer) => {
      const retailerName = retailerNamesByIds[retailer.retailer_id];
      keys.add(retailerName);
      retailersData[retailerName] = Number((retailer.gmv[currentCurrency] / 100).toFixed(2));
      return retailersData;
    }, {});

    uiData.gmv.push({
      model,
      productName,
      productImage,
      ...retailersData,
    });
  });

  rawData.units.forEach((modelRecord) => {
    const { model, retailers, product_image: productImage, product_name: productName } = modelRecord;
    const retailersData = retailers.reduce((retailersData, retailer) => {
      const { retailer_id: retailerId, count } = retailer;
      const retailerName = retailerNamesByIds[retailerId];
      keys.add(retailerName);
      retailersData[retailerName] = count;
      return retailersData;
    }, {});

    uiData.units.push({
      model,
      productName,
      productImage,
      ...retailersData,
    });
  });

  /* essentially the same as units */
  (rawData.orders || []).forEach((modelRecord) => {
    const { model, retailers, product_image: productImage, product_name: productName } = modelRecord;
    const retailersData = retailers.reduce((retailersData, retailer) => {
      const { retailer_id: retailerId, count } = retailer;
      const retailerName = retailerNamesByIds[retailerId];
      keys.add(retailerName);
      retailersData[retailerName] = count;
      return retailersData;
    }, {});

    uiData.orders.push({
      model,
      productName,
      productImage,
      ...retailersData,
    });
  });

  uiData.gmv.sort(byBestSellers);
  uiData.units.sort(byBestSellers);
  uiData.orders.sort(byBestSellers);

  return { uiData, keys: Array.from(keys) };
}

export function prepareDataForSalesGraph({ dates, groupedByDate, retailerNamesByIds, currency }) {
  const { start: startDate, end: endDate } = dates;
  const data = { gmv: [], units: [], orders: [] };

  Object.entries(retailerNamesByIds).forEach(([salesChannelId, id]) => {
    data.gmv.push({
      id,
      data: [],
      salesChannelId,
    });
    data.units.push({
      id,
      data: [],
      salesChannelId,
    });
    data.orders.push({
      id,
      data: [],
      salesChannelId,
    });
  });

  for (
    let currentDate = startDate;
    differenceInDays(endDate, currentDate) >= 0;
    currentDate = addDays(currentDate, 1)
  ) {
    if (currentDate > endDate) {
      break;
    }

    const currentDateKey = formatDateGraphXAxis(currentDate, shouldShowYearsInXAxis(startDate, endDate));

    Object.keys(retailerNamesByIds).forEach((retailerId) => {
      const currentGmvRetailer = data.gmv.find(({ salesChannelId }) => salesChannelId === retailerId).data;
      const gmvValue = groupedByDate[currentDateKey]?.[retailerId]
        ? get(groupedByDate[currentDateKey][retailerId], [DATA_TYPE_GMV.value, currency, 'all_orders'], 0)
        : 0;

      const currentGmvData = currentGmvRetailer.find(({ x }) => x === currentDateKey);
      if (currentGmvData) {
        currentGmvData.y += toUiSalesValue(gmvValue);
      } else {
        currentGmvRetailer.push({
          x: currentDateKey,
          y: toUiSalesValue(gmvValue),
          fullDate: currentDate,
        });
      }

      const currentUnitsRetailer = data.units.find(({ salesChannelId }) => salesChannelId === retailerId).data;
      const unitsValue = groupedByDate[currentDateKey]?.[retailerId]
        ? get(groupedByDate[currentDateKey][retailerId], [DATA_TYPE_UNITS.value, 'all_orders'], 0)
        : 0;

      const currentUnitsData = currentUnitsRetailer.find(({ x }) => x === currentDateKey);
      if (currentUnitsData) {
        currentUnitsData.y += unitsValue;
      } else {
        currentUnitsRetailer.push({
          x: currentDateKey,
          y: unitsValue,
          fullDate: currentDate,
        });
      }

      const currentOrdersRetailer = data.orders.find(({ salesChannelId }) => salesChannelId === retailerId).data;

      const ordersValue = groupedByDate[currentDateKey]?.[retailerId]
        ? get(groupedByDate[currentDateKey][retailerId], 'total_orders_count', 0)
        : 0;

      const currentOrdersData = currentOrdersRetailer.find(({ x }) => x === currentDateKey);

      if (currentOrdersData) {
        currentOrdersData.y += ordersValue;
      } else {
        currentOrdersRetailer.push({
          x: currentDateKey,
          y: ordersValue,
          fullDate: currentDate,
        });
      }
    });
  }
  return data;
}

export function extractCountriesInfo(countries) {
  return countries.reduce((countries, country) => {
    let countryInfo;
    try {
      countryInfo = byIso(country);
    } catch {
      countryInfo = byInternet(country);
    }
    countries[country] = countryInfo?.country || country;
    return countries;
  }, {});
}

export const getInitialReportState = (reportColumns, reportType) => {
  const fullyConfiguredDefaultReport = {
    ...DEFAULT_REPORT_STATE,
    visibleColumns: getDefaultColumns(reportColumns),
  };

  switch (reportType) {
    case REPORT_TYPES.SALES_BY_PRODUCT:
      return {
        ...fullyConfiguredDefaultReport,
        sort: {
          column: SortKey.MODEL,
          direction: SortDirection.DESC,
        },
      };
    case REPORT_TYPES.RETURNS:
      return {
        ...fullyConfiguredDefaultReport,
        sort: {
          column: SortKey.RETURNED_AT,
          direction: SortDirection.DESC,
        },
      };
    case REPORT_TYPES.CANCELLATIONS:
      return {
        ...fullyConfiguredDefaultReport,
        sort: {
          column: SortKey.CANCELLED_AT,
          direction: SortDirection.DESC,
        },
      };
    default:
      return fullyConfiguredDefaultReport;
  }
};

export function roundDollarsFromCents(value) {
  return Math.round(value / 100) * 100;
}

export function getIsAnalyticsEnabled({ featureFlagEmailBasedValue, userEmail, isEnabledFromSupplierSettings }) {
  const authorizedEmails = jsonStringToObject(featureFlagEmailBasedValue, []);
  const isAllowedByEmail = userEmail && Array.isArray(authorizedEmails) && authorizedEmails.includes(userEmail);

  return isAllowedByEmail || isEnabledFromSupplierSettings;
}

export function translateSortKeyToBackend(column, currency) {
  const translate = {
    date: SortKey.ORDER_CREATED_AT,
    returnedAt: SortKey.RETURNED_AT,
    cancellationDate: SortKey.CANCELLED_AT,
    ordersCount: 'totalOrderCount',
    unitsCount: 'unitCount-allOrders',
    gmv: `gmv-${currency}-allOrders`,
    returnedUnitsCount: 'unitCount-returns',
    returnedValue: `gmv-${currency}-returns`,
    cancelledUnitsCount: 'unitCount-cancelledOrders',
    cancellationsValue: `gmv-${currency}-cancelledOrders`,
    model: 'model',
    productName: 'productTitle',
    returnedUnits: 'unitCount-returns',
    totalRefund: `gmv-${currency}-returns`,
    totalOrderCount: SortKey.ORDERS_COUNT,
  };
  return translate[column] || SortKey.ORDER_CREATED_AT;
}

export function useShortDateInTooltip(allData) {
  if (!allData?.gmv?.length) return true;
  const firstRetailer = Object.values(allData.gmv)?.[0];
  if (!firstRetailer?.data?.length) return true;

  const startDate = firstRetailer.data[0].fullDate;
  const endDate = firstRetailer.data.at(-1).fullDate;
  return shouldShowYearsInXAxis(startDate, endDate);
}

export function shouldShowYearsInXAxis(startDate, endDate) {
  return differenceInCalendarYears(endDate, startDate) >= 1;
}

export function sortRetailerIdsByTotals({ totalsByRetailerId, isGmvMetric, currentOrderType }) {
  return Object.entries(totalsByRetailerId)
    .map(([retailerId, total]) => [retailerId, total])
    .sort(({ gmv: gmvA, units: unitsA, orders: ordersA }, { gmv: gmvB, units: unitsB, orders: ordersB }) => {
      if (isGmvMetric) return gmvB - gmvA;
      if (currentOrderType === AnalyticsOrderType.UNITS) return unitsB - unitsA;
      return ordersB - ordersA;
    });
}
